import { ICellRendererParams } from '@ag-grid-community/core';

import { FastLaneItems } from '../../../../fastlane/models/fastlane-datacenter-config';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef,
	DateColDef,
	DimensionColDef,
	LinkColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterDimensionFilterComponent } from '../../filter-type/datacenter-dimension-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';

const linkToPrototypeCardCellRendererParams = {
	routerLink(params: ICellRendererParams<FastLaneItems, unknown>) {
		if (!params.data) {
			return null;
		}
		const { data } = params;
		return {
			commands: [
				'/fastlane',
				'prototype',
				'card',
				data.id
			]
		};
	}
};

export class PrototypeDatacenterColumnDefinition {
	readonly PROTOTYPE_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<FastLaneItems> |
	CommonColDef<FastLaneItems, string>>;

	constructor(private readonly translationService: TranslationService) {
		this.PROTOTYPE_DATA_CENTER_DEFAULT_CONFIGURATION = new Map<string, CommonColDef<FastLaneItems> |
		CommonColDef<FastLaneItems, string>>([
			[
				'customerRequestName',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.customer-req-name'),
					field: 'customerRequestName',
					colId: 'customerRequestName',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'soldTo',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					field: 'soldTo',
					colId: 'soldTo',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['soldTo', 'soldToDescription'] },
					suppressHeaderMenuButton: true,
					minWidth: 120,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.soldToDescription;
					}
				})
			],
			[
				'thickness',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.thickness'),
					field: 'thickness',
					colId: 'thickness',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'width',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.width'),
					field: 'width',
					colId: 'width',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'supplierRfqNumber',
				new LinkColDef<FastLaneItems>({
					headerName: this.translationService.getTR('application.procurement.supplier.rfqNumber'),
					field: 'supplierRfqNumber',
					colId: 'supplierRfqNumber',
					filter: DatacenterTextFilterComponent,
					cellRendererParams: linkToPrototypeCardCellRendererParams,
					minWidth: 130,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'creationDate',
				new DateColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.creation-date'),
					colId: 'creationDate',
					field: 'creationDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'prototypeName',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.proto-name'),
					field: 'prototypeName',
					colId: 'prototypeName',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'prototypeStatus',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.proto-status'),
					field: 'prototypeStatus',
					colId: 'prototypeStatus',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['prototypeStatus', 'prototypeStatusDescription'] },
					suppressHeaderMenuButton: true,
					minWidth: 120,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.prototypeStatusDescription;
					}
				})
			],
			[
				'requestedSteelGrade',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-shipment.grade'),
					field: 'requestedSteelGrade',
					colId: 'requestedSteelGrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					minWidth: 150,
					maxWidth: 150,
					width: 150,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'form',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('eoffer.form'),
					field: 'form',
					colId: 'form',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['form', 'formDescription'] },
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.formDescription;
					}
				})
			],
			[
				'prototypeDimension',
				new DimensionColDef<FastLaneItems>({
					headerName: this.translationService.getTR('scan.dimensions'),
					dimensionGetter(params) {
						return params.data;
					},
					colId: 'prototypeDimension',
					filter: DatacenterDimensionFilterComponent,
					fields: ['thickness', 'width', 'length'],
					suppressHeaderMenuButton: false,
					minWidth: 150,
					maxWidth: 150,
					width: 150,
					columnType: ColumnType.DIMENSION_COLUMN
				})
			],
			[
				'requestedQuantity',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('eoffer.quantity'),
					colId: 'requestedQuantity',
					field: 'requestedQuantity',
					fields: ['requestedQuantity', 'requestedQuantityUnitDescription'],
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const item = params.data;
						if (!item.requestedQuantity) {
							return '';
						}
						let value = item.requestedQuantity.toString();
						value += item.requestedQuantityUnitDescription
							? ` ${ item.requestedQuantityUnitDescription }`
							: '';
						return value;
					},
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'rddWeek',
				new DateColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.rdd-week'),
					colId: 'rddWeek',
					field: 'requestedDeliveryDate',
					minWidth: 84,
					maxWidth: 84,
					width: 84,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'description',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.req-desc'),
					colId: 'description',
					field: 'description',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'customerRefusal',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.customerRefusal'),
					colId: 'customerRefusal',
					field: 'customerRefusal',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'lastModifiedDate',
				new DateColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.LMD'),
					colId: 'lastModifiedDate',
					field: 'lastModifiedDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'unitWeight',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('fastlane.itemlist.product.blank_weight'),
					field: 'unitWeight',
					colId: 'unitWeight',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'sheetWeight',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('fastlane.wizard.items.details.sheet.weight'),
					field: 'sheetWeight',
					colId: 'sheetWeight',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'coating',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('product.title.group.coating'),
					field: 'coating',
					colId: 'coating',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'innerDiameter',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('fastlane.itemlist.coil.inner.diameter'),
					field: 'innerDiameter',
					colId: 'innerDiameter',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'length',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('batch.length'),
					field: 'length',
					colId: 'length',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'outsideDiameter',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('need4steel-product-details.max-outside-Diameter'),
					field: 'outsideDiameter',
					colId: 'outsideDiameter',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'oiling',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('fastlane.itemlist.productcharacteristic.oilquantity'),
					field: 'oiling',
					colId: 'oiling',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'stockist',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.stockist'),
					field: 'stockist',
					colId: 'stockist',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'currency',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('issue.currency'),
					field: 'currency',
					colId: 'currency',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['currency', 'currency'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.currency;
					}
				})
			],
			[
				'coilAxis',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('fastlane.itemlist.product.coil_axis'),
					field: 'coilAxis',
					colId: 'coilAxis',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['coilAxis', 'coilAxisDescription'] },
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.coilAxisDescription;
					}
				})
			],
			[
				'incoterm',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('schedule-business-order.incoterm'),
					field: 'incoterm',
					colId: 'incoterm',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['incoterm', 'incoterm'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.incoterm;
					}
				})
			],
			[
				'transportMode',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('datacenter-filter.transport-mode'),
					field: 'transportMode',
					colId: 'transportMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { fastlaneFields: ['transportMode', 'transportModeDescription'] },
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const fastLaneItems = params.data;
						return fastLaneItems.transportModeDescription;
					}
				})
			],
			[
				'totalTonnageCalculated',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('claim.total.weight'),
					field: 'totalTonnageCalculated',
					colId: 'totalTonnageCalculated',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'pricePerUnit',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('eoffer.price'),
					field: 'pricePerUnit',
					colId: 'pricePerUnit',
					filter: DatacenterNumberFilterComponent,
					valueGetter: params => {
						if (!params.data) {
							return 0;
						}
						const item = params.data;
						if (!item.pricePerUnit) {
							return 0;
						}
						return item.pricePerUnit;
					},
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'shipToDescription',
				new CommonColDef<FastLaneItems>({
					headerName: this.translationService.getTR('eoffer.shipto'),
					field: 'shipToDescription',
					colId: 'shipToDescription',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
		]);
	}
}

export const DEFAULT_VIEW_PROTOTYPE_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerRequestName',
			pinned: 'left'
		},
		{
			colId: 'supplierRfqNumber',
			pinned: 'left'
		},
		{
			colId: 'lastModifiedDate',
			pinned: 'left'
		},
		{
			colId: 'prototypeName'
		},
		{
			colId: 'prototypeStatus'
		},
		{
			colId: 'requestedSteelGrade'
		},
		{
			colId: 'form'
		},
		{
			colId: 'prototypeDimension'
		},
		{
			colId: 'requestedQuantity'
		},
		{
			colId: 'rddWeek'
		}
	],
	sortModel: [
		{
			colId: 'lastModifiedDate',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'prototypes',
	isDefault: true,
	name: 'Default',
	id: -99
};
